import { alpha, createTheme } from "@mui/material/styles";
import { amber, grey } from "@mui/material/colors";

// The brand colours, the same in both modes: the navy, and the two halves of the
// Fusion MSI star. For brand decoration like the sign-in page's glow.
const brand = {
  navy: "#1B4584",
  blue: "#117ACA",
  gold: "#D4AF37",
};

// Light mode: the brand navy and blue on white.
const lightPalette = {
  brand,
  primary: {
    main: "#1B4584",
  },
  secondary: {
    main: "#117ACA",
  },
  background: {
    sidebar: grey[50],
  },
  // The current page in the sidebar: a soft tint of the lighter brand blue rather
  // than a solid navy block.
  navSelected: {
    main: alpha("#117ACA", 0.12),
    contrastText: "#0D5F9E",
  },
};

// Dark mode: MUI's stock dark greys with the logo's two colours. The brand navy is
// too dark to read on a dark background, so primary is a light brand blue, and
// Fusion MSI gold is the smaller accent (secondary, the current sidebar item).
const darkPalette = {
  mode: "dark",
  brand,
  primary: {
    main: "#6FB1F5",
  },
  secondary: {
    main: "#D4AF37",
    dark: "#B08A28",
    contrastText: "rgba(0, 0, 0, 0.87)",
  },
  // MUI's dark greys nudged cool, toward the brand blue (stock is a neutral #121212).
  // The sidebar sits one shade off the page so the nav reads as its own surface.
  background: {
    default: "#101318",
    paper: "#101318",
    sidebar: "#181C22",
  },
  navSelected: {
    main: alpha("#D4AF37", 0.16),
    contrastText: "#D4AF37",
  },
};

// The ClinicalHUD2 glasses preview. The Even G2 lens shows a single green, so the text stays
// green; everything around it is only the drawing of the lens. Same in both modes on purpose,
// since it imitates a physical screen. Only GlassesHudPreview reads it.
const hudPalette = {
  background: "#0D1613",
  backgroundEdge: "#060B09",
  foreground: "#8EF5B8",
  glow: "rgba(110, 245, 170, 0.35)",
  frame: "#2B3A34",
};

// A perfect MorningReport case: the gold star on the case list and the score.
// MUI's warning is orange, so gold is its own token. Lighter in dark mode.
const goldPalette = {
  light: { main: amber[600], contrastText: "rgba(0, 0, 0, 0.87)" },
  dark: { main: amber[400], contrastText: "rgba(0, 0, 0, 0.87)" },
};

const themeOptions = {
  components: {
    MuiButton: {
      defaultProps: {
        variant: "text",
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(4px)",
          },
        },
        // Dark mode: the sidebar's shade, not MUI's elevation overlay, which lifts a
        // dialog to a washed-out grey.
        paper: ({ theme }) =>
          theme.palette.mode === "dark" && {
            backgroundImage: "none",
            backgroundColor: theme.palette.background.sidebar,
          },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Federo", sans-serif',
    },
    h2: {
      fontFamily: '"Federo", sans-serif',
    },
    h3: {
      fontFamily: '"Federo", sans-serif',
    },
    h4: {
      fontFamily: '"Federo", sans-serif',
    },
    h5: {
      fontFamily: '"Federo", sans-serif',
    },
    // The Fusion MSI name beside the mark (Logo.js). Federo has no bold cut, so the
    // weight is synthesised by the browser; it reads closer to the client's mark.
    logo: {
      fontFamily: '"Federo", sans-serif',
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 1,
    },
  },
};

export const getMuiTheme = (mode = "light") =>
  createTheme({
    ...themeOptions,
    palette:
      mode === "dark"
        ? { ...darkPalette, hud: hudPalette, gold: goldPalette.dark }
        : { ...lightPalette, hud: hudPalette, gold: goldPalette.light },
  });

export const muiTheme = getMuiTheme("light");
